import { Select } from 'ol/interaction';
import Draw, { createBox } from 'ol/interaction/Draw';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { pointerMoveListener } from './erasorPointer';
import { containsExtent } from 'ol/extent';
import { Feature } from 'ol/index';
import { Polygon } from 'ol/geom';

import { polygonPositionsToMapCoordinates } from './helpers';

import { setTranslateInteraction } from './annotation/translate.interaction';
import { setModifyInteraction } from './annotation/modify.interaction';
import {
	annotationStyles,
	isSelectableBox,
	selectedBoxId,
	stampAnnotationNode,
} from './annotation/annotation.interaction';
import { createSelectFirstSession } from './annotation/selectFirst';
import { MODEL_TYPE } from '@utils/constants';

/**
 * Adds a drawing layer to the map with specified drawing interactions and styles.
 *
 * @param {Object} map - The OpenLayers map instance.
 * @param {string} drawType - The type of drawing interaction (e.g., 'Point', 'LineString', 'Polygon').
 * @param {string} drawTool - The drawing tool to be used.
 * @param {Object} colorOptions - The color options for the drawing style.
 * @param {boolean} isRotateEnabled - Whether rotation is enabled for modify interaction.
 * @param {boolean} [scoped] - Select-first editing (Collection view): a box is moved or reshaped
 *   only once selected, and the Draw / Erase pair is replaced by the Draw / Select modes. The
 *   default keeps the legacy always-on Modify and Translate.
 * @returns {Object} - An object containing references to the layer, source, draw interaction, select interaction, translate interaction, transform interaction and pointer move event handler.
 */
export const addDrawLayer = ({
	map,
	drawType,
	drawTool,
	colorOptions,
	isRotateEnabled = false,
	scoped = false,
}) => {
	// Create a new vector source for the drawing layer
	const source = new VectorSource({ wrapX: false });

	// Create a new vector layer with the specified source and style function
	const layer = new VectorLayer({
		source: source,
		renderBuffer: 500,
		zIndex: 25,
		name: 'drawLayer',
		// The resolution decides whether the scoped session draws box labels.
		style: (feature, resolution) => annotationStyles({ feature, colorOptions, resolution }),
	});
	map.addLayer(layer);

	if (scoped)
		return addScopedDrawLayer({
			map,
			source,
			layer,
			drawType,
			drawTool,
			colorOptions,
			isRotateEnabled,
		});

	// Initialize the draw interaction variable
	let draw = null;

	// Set up the draw interaction with the specified parameters
	draw = setDrawInteraction({
		map,
		draw,
		source,
		drawType,
		drawTool,
		colorOptions,
	});

	// Track the currently selected feature for copy-paste
	let selectedFeature = null;
	let lastInteractedFeature = null; // Keep track of the last feature that was interacted with

	/**
	 * Updates the currently selected feature for copy/paste operations
	 * @param {Feature|null} feature - The feature to select, or null to clear
	 * @param {Object} options - Configuration options
	 * @param {boolean} options.updateLastInteracted - Whether to also update lastInteractedFeature (default: true)
	 */
	const setSelectedFeature = (feature, { updateLastInteracted = true } = {}) => {
		selectedFeature = feature;
		if (updateLastInteracted && feature) lastInteractedFeature = feature;
	};

	// Create a select interaction for the drawing layer
	const select = new Select({
		layers: [layer],
		style: function (feature) {
			// Return the original style of the feature
			return feature.getStyle();
		},
	});
	map.addInteraction(select);
	select.setActive(false); // Initially deactivate the select interaction

	const translate = setTranslateInteraction({
		layer: layer,
		updateModifiedObjects: () => {}, // not needed for single image annotation
	});
	map.addInteraction(translate);

	const modify = setModifyInteraction({
		source: source,
		updateModifiedObjects: () => {}, // not needed for single image annotation
		isRotateEnabled: isRotateEnabled,
	});
	map.addInteraction(modify);

	// Track selected feature when modify or translate starts
	modify.on('modifystart', event => {
		setSelectedFeature(event.features.item(0));
		// console.log('[Selection] Feature selected via modify:', selectedFeature);
	});

	translate.on('translatestart', event => {
		setSelectedFeature(event.features.item(0));
		// console.log('[Selection] Feature selected via translate:', selectedFeature);
	});

	// Don't clear immediately - keep the last interacted feature available
	modify.on('modifyend', event => {
		setSelectedFeature(event.features.item(0));
		// console.log('[Selection] Modify ended, feature still available:', selectedFeature);
	});

	translate.on('translateend', event => {
		setSelectedFeature(event.features.item(0));
		// console.log('[Selection] Translate ended, feature still available:', selectedFeature);
	});

	// Clear selection when drawing a new feature
	draw.on('drawstart', () => {
		setSelectedFeature(null, { updateLastInteracted: false });
		// console.log('[Selection] Drawing new feature, cleared selection');
	});

	// Create a pointermove event listener to change the cursor to a trash can
	const pointerMoveRef = evt => {
		pointerMoveListener(evt, map);
	};

	// Function to get the currently selected feature
	// Returns the actively selected feature, or the last interacted feature
	const getSelectedFeature = () => {
		const feature = selectedFeature || lastInteractedFeature;
		// console.log('[Selection] getSelectedFeature called, returning:', feature);
		return feature;
	};

	// Return an object containing the references
	return {
		layer,
		source,
		draw,
		select,
		translate,
		modify,
		pointerMoveRef,
		getSelectedFeature,
		setSelectedFeature,
	};
};

/** A box moved or reshaped in a scoped session is no longer what the session saved. */
const markModified = feature => {
	if (feature?.get('uuid')) feature.set('isModified', true);
	if (feature?.get('saved')) feature.set('saved', false);
};

/**
 * The scoped layer: the select-first controller (Modify and Translate on the
 * selected box only, click-to-select, undo entries) around a Draw that refuses
 * to start on a selectable box. No Select / eraser interaction.
 */
const addScopedDrawLayer = ({
	map,
	source,
	layer,
	drawType,
	drawTool,
	colorOptions,
	isRotateEnabled,
}) => {
	const session = createSelectFirstSession({
		map,
		source,
		layer,
		isSelectable: isSelectableBox,
		updateModified: markModified,
		rotateEnabled: isRotateEnabled,
	});

	const draw = setDrawInteraction({
		map,
		draw: null,
		source,
		drawType,
		drawTool,
		colorOptions,
		session,
	});

	// The copy-paste shortcut copies the selected box (not the last one touched).
	const getSelectedFeature = () =>
		selectedBoxId
			? (source.getFeatures().find(feature => feature.get('boxId') === selectedBoxId) ?? null)
			: null;

	return {
		layer,
		source,
		draw,
		select: null,
		translate: session.getTranslate(),
		modify: session.getModify(),
		pointerMoveRef: null,
		getSelectedFeature,
		setSelectedFeature: () => {},
		session,
	};
};

// Sources a scoped session already guards against empty boxes (Draw is rebuilt often).
const guardedSources = new WeakSet();

/**
 * Removes a feature from the source if it has an invalid geometry.
 * A geometry is considered invalid if its area is less than or equal to zero.
 *
 * @param {Object} params - The parameters object
 * @param {ol.source.Vector} params.target - The vector source from which to remove the feature
 * @param {ol.Feature} params.feature - The feature to validate and potentially remove
 * @returns {void}
 */
const removeInvalidFeature = ({ target: source, feature }) => {
	let isInvalid = false;
	const geometry = feature.getGeometry();
	if (geometry?.getArea() <= 0) isInvalid = true;

	if (isInvalid) source.removeFeature(feature);
};

/**
 * Sets up a draw interaction on the map.
 *
 * @param {Object} map - The OpenLayers map instance.
 * @param {Object} draw - The existing draw interaction, if any.
 * @param {Object} source - The vector source for the draw interaction.
 * @param {string} drawType - The type of geometry to be drawn (e.g., 'Point', 'LineString', 'Polygon', 'Circle').
 * @param {string} drawTool - The tool to be used for drawing (e.g., 'Box').
 * @param {Object} [session] - A scoped select-first session: the new Draw gets its condition (no
 *   sketch on a selectable box) and reports into it, replacing the previous Draw's reports.
 * @returns {Object} - The new draw interaction.
 */
export const setDrawInteraction = ({
	map,
	draw,
	source,
	drawType,
	drawTool,
	colorOptions,
	activeClassId,
	session = null,
}) => {
	// If there is an existing draw interaction, dispose of it and remove it from the map
	if (draw) {
		draw.dispose();
		map.removeInteraction(draw);
	}

	// Create a new draw interaction with the specified parameters
	draw = new Draw({
		source: source, // The vector source where the drawn features will be stored
		type: drawType, // The type of geometry to be drawn
		freehand: drawTool === 'Freehand',
		geometryFunction: drawType === 'Circle' && drawTool === 'Box' ? createBox() : null, // Use a custom geometry function if drawing a box
		style: feature => annotationStyles({ feature, activeClassId, colorOptions }),
		...(session && { condition: session.drawCondition }),
	});

	// Automatically remove invalid features that are added to the source
	// (once per source in a scoped session, which rebuilds Draw on every tool change)
	if (!session || !guardedSources.has(source)) {
		source.on('addfeature', removeInvalidFeature);
		guardedSources.add(source);
	}

	// Add the new draw interaction to the map
	map.addInteraction(draw);
	session?.attachDraw(draw);

	// Return the new draw interaction
	return draw;
};

/**
 * Handles the start of a drawing interaction.
 *
 * @param {Object} e - The event object from the drawing interaction.
 * @param {Object} layer - The layer on which the drawing is happening.
 * @param {Object} draw - The draw interaction instance.
 * @param {number} classId - The class ID to be assigned to the feature.
 * @param {string} [tileName] - The image the box belongs to, stamped for the session helpers.
 * @param {boolean} [abortOutside] - Abort a sketch starting outside the image (scoped sessions,
 *   whose select-first controller must see the sketch end) instead of removing its last point.
 */
export const drawStartInteraction = ({
	e,
	layer,
	draw,
	classId,
	activateInteractions,
	tileName,
	abortOutside = false,
}) => {
	// Get the feature being drawn from the event object
	const feature = e.feature;

	// Check if the feature starts inside the image extent
	const startsInside = isFeatureInsideExtent({ feature, layer });
	if (!startsInside) {
		// If the feature does not start inside the image extent, remove the last point and prevent the default action
		if (abortOutside) draw.abortDrawing();
		else draw.removeLastPoint();
		e.preventDefault();

		return;
	}

	activateInteractions(false);

	// Set the classId property on the feature to add the correct styling based on class
	feature.setProperties({
		classId,
		isInside: true, // isInside is always true for single image annotation
	});
	stampAnnotationNode(feature);
	if (tileName) feature.set('tileName', tileName, true);
};

/**
 * Handles the end of a drawing interaction and assigns the class ID and annotation type to the feature.
 *
 * @param {Object} e - The event object from the drawing interaction.
 * @param {number} classId - The class ID to be assigned to the feature.
 * @param {string} modelType - The type of annotation being drawn.
 */
export const drawEndInteraction = ({ e, classId, modelType, activateInteractions }) => {
	// Get the feature that was drawn from the event object
	const feature = e.feature;

	// Get the type of the geometry
	const geometry = feature.getGeometry();
	let geometryType = geometry.getType().toLowerCase();

	// If the geometry type is a polygon and the annotation type is object detection,
	// convert the geometry type to bbox
	if (geometryType === 'polygon' && modelType === MODEL_TYPE.OBJECT_DETECTION) {
		geometryType = 'bbox';
	}

	// Set the classId and annotation_type properties on the feature
	feature.setProperties({
		classId,
		annotation_type: geometryType,
		isInside: true,
	});

	activateInteractions(true);
};

/**
 * The open image as a GeoJSON square, so the scoped session treats the whole image as its
 * "tile": a box is selectable when it lies in the image. The tile name is the image's.
 *
 * @param {number[]} extent - the image extent [minX, minY, maxX, maxY]
 * @param {string} tileName - the image file name
 * @returns {{ type: 'Feature', properties: { tile_name: string }, geometry: object }}
 */
export const imageExtentToSquare = (extent, tileName) => {
	const [minX, minY, maxX, maxY] = extent;
	return {
		type: 'Feature',
		properties: { tile_name: tileName },
		geometry: {
			type: 'Polygon',
			coordinates: [
				[
					[minX, minY],
					[minX, maxY],
					[maxX, maxY],
					[maxX, minY],
					[minX, minY],
				],
			],
		},
	};
};

/**
 * Normalizes polygon coordinates based on the image extent.
 *
 * @param {number[]} imageExtent - The extent of the image [minX, minY, maxX, maxY]
 * @param {import("ol").Feature} feature - The OpenLayers feature containing a polygon geometry
 * @returns {Array<number[]>}  Array of normalized coordinates as [x, y] pairs in 0-1 range
 */
export const getNormalizedPolygonByExtent = (imageExtent, feature) => {
	const coordinates = feature.getGeometry().getCoordinates()[0];

	// Normalize the coordinates based on the current image extent
	return coordinates.map(coord => {
		const normalizedX = (coord[0] - imageExtent[0]) / (imageExtent[2] - imageExtent[0]);

		const normalizedY = 1 - (coord[1] - imageExtent[1]) / (imageExtent[3] - imageExtent[1]);

		// Values can not be less than 0 or greater than 1
		const clampedX = Math.max(0, Math.min(1, normalizedX));
		const clampedY = Math.max(0, Math.min(1, normalizedY));

		return [clampedX, clampedY];
	});
};

/**
 * Parses a feature into an annotation object
 *
 * @param {Array} currentImageExtent - The extent of the current image.
 * @param {Object} feature - The feature being annotated.
 * @param {string} tile_name - The name of the tile where the annotation is being added.
 * @param {string} taskId - The ID of the task associated with the annotation.
 * @returns {Object} - The new annotation object.
 */
export const parseFeatureToAnnotation = ({ currentImageExtent, feature, tile_name, taskId }) => {
	// If tile_name or feature is not provided, exit the function
	if (!tile_name && !feature) return;

	const { uuid = 'unknown', annotation_type, classId } = feature.getProperties();

	// Initialize a new annotation object with default properties
	let newAnnotation = {
		crs_coordinates: false,
		task_uuid: taskId,
		machine_annotation: uuid === 'unknown' ? false : true,
		deprecated: false,
		human_input: true,
		annotation_type,
		classid: classId,
		tile_name,
		uuid,
	};

	// Handle circle annotations
	if (annotation_type === 'circle') {
		// Get the center of the circle
		const center = feature.getGeometry().getCenter();
		// Normalize the center coordinates based on the current image extent
		const pixelCoordinateReversed = [
			(center[0] - currentImageExtent[0]) / (currentImageExtent[2] - currentImageExtent[0]),
			1 -
				(center[1] - currentImageExtent[1]) /
					(currentImageExtent[3] - currentImageExtent[1]),
		];

		// Values can not be less than 0 or greater than 1
		const coordinatesClamped = pixelCoordinateReversed.map(coord =>
			Math.max(0, Math.min(1, coord))
		);

		// Update the new annotation object with circle-specific properties
		newAnnotation = {
			...newAnnotation,
			x_center: coordinatesClamped[0],
			y_center: coordinatesClamped[1],
			radius:
				feature.getGeometry().getRadius() / (currentImageExtent[2] - currentImageExtent[0]),
		};
	} else if (
		annotation_type === 'polygon' ||
		annotation_type === 'bbox' ||
		annotation_type === 'rotated_bbox'
	) {
		const normalizedCoordinates = getNormalizedPolygonByExtent(currentImageExtent, feature);

		// Update the new annotation object with polygon-specific properties
		newAnnotation = {
			...newAnnotation,
			coordinates: normalizedCoordinates,
		};
	}

	return newAnnotation;
};

/**
 * Deletes an annotation feature from the source
 *
 * @param {Object} feature - The feature to be deleted.
 * @param {Object} source - The vector source from which the feature will be removed.
 */
export const deleteAnnotation = ({ feature, source }) => {
	console.log('removed annotation');

	// Remove the feature from the vector source
	source.removeFeature(feature);
};

/**
 * Adds existing annotations to the source
 *
 * @param {Object} source - The vector source to which the features will be added.
 * @param {Array} annotations - The array of annotations to be added.
 * @param {Array} imageExtent - The extent of the current image.
 */
export const addExistingAnnotations = ({ source, annotations, imageExtent }) => {
	// If annotations or source is not provided, exit the function
	if (!annotations || !source) return;

	// Iterate over each annotation in the annotations array
	annotations.forEach(annotation => {
		try {
			// Create a feature from the annotation
			const coordinates = annotation?.properties?.bbox;
			const { classid, annotation_uuid } = annotation.properties;

			const polygonPositionTranslatedToMapCoordinates = polygonPositionsToMapCoordinates(
				coordinates,
				imageExtent
			);

			const feature = new Feature({
				geometry: new Polygon(polygonPositionTranslatedToMapCoordinates),
				uuid: annotation_uuid,
				classId: classid,
				annotation_type: 'bbox',
				isInside: true,
			});

			// If the feature was successfully created, add it to the source
			if (feature) {
				// Add the feature to the vector source
				source.addFeature(feature);
			}
		} catch (error) {
			console.error('Could not add existing detection as annotation to map', error);
		}
	});
};

/**
 * Checks if a feature's geometry is inside the current image extent.
 *
 * @param {Object} feature - The feature whose geometry is being checked.
 * @param {Object} layer - The layer containing the current image extent.
 * @returns {boolean} - True if the feature's geometry is inside the current image extent, false otherwise.
 */
const isFeatureInsideExtent = ({ feature, layer }) => {
	// Get the current image extent from the layer
	const currentImageExtent = getCurrentImageExtent(layer);

	// Get the geometry of the feature
	const geometry = feature.getGeometry();
	// Check if the geometry's extent is contained within the current image extent
	const isInside = containsExtent(currentImageExtent, geometry.getExtent());

	// Return the result of the containment check
	return isInside;
};

/**
 * Retrieves the current image extent from the given layer.
 *
 * @param {Object} layer - The layer from which to get the current image extent.
 * @returns {Array} - The extent of the current image.
 */
const getCurrentImageExtent = layer => {
	// Get the source of the current image from the layer
	const currentImageSource = layer.getSource();

	// Get the extent of the current image from the source
	const currentImageExtent = currentImageSource?.getImageExtent();

	// Return the current image extent
	return currentImageExtent;
};
